import { App, DatePicker, Spin } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MobileEmpty } from '@/components/mobile/MobileEmpty';
import { MobileReportCard } from '@/components/mobile/MobileReportCard';
import { formatLeadershipTier } from '@/constants/leadership-tier';
import { ROUTES } from '@/constants/routes';
import * as departmentsApi from '@/services/api/departments';
import * as leadershipReportsApi from '@/services/api/leadership-reports';
import { ApiError } from '@/services/api/client';
import type { ReportRecord } from '@/types/report';
import { flattenDepartmentTree } from '@/utils/department-tree';
import { getLocalDateKey } from '@/utils/date';

export function LeadershipReportsMobilePage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [reportDate, setReportDate] = useState<Dayjs>(() => dayjs());
  const [loading, setLoading] = useState(false);
  const [departmentItems, setDepartmentItems] = useState<ReportRecord[]>([]);
  const [departmentNameMap, setDepartmentNameMap] = useState<Map<string, string>>(new Map());

  const dateKey = reportDate.format('YYYY-MM-DD');

  const loadDepartments = useCallback(async () => {
    try {
      const tree = await departmentsApi.fetchDepartmentTree();
      const map = new Map<string, string>();
      for (const dept of flattenDepartmentTree(tree)) {
        map.set(dept.id, dept.name);
      }
      setDepartmentNameMap(map);
    } catch {
      setDepartmentNameMap(new Map());
    }
  }, []);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const result = await leadershipReportsApi.fetchLeadershipReports({
        page: 1,
        pageSize: 100,
        startDate: dateKey,
        endDate: dateKey,
      });
      setDepartmentItems(result.departmentLeaderReports.items);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载监管日报失败';
      message.error(text);
      setDepartmentItems([]);
    } finally {
      setLoading(false);
    }
  }, [dateKey, message]);

  useEffect(() => {
    void loadDepartments();
  }, [loadDepartments]);

  useEffect(() => {
    void loadReports();
  }, [loadReports]);

  const reportList = useMemo(() => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      );
    }
    if (departmentItems.length === 0) {
      return (
        <MobileEmpty
          message={dateKey === getLocalDateKey() ? '今日暂无已提交日报' : '该日期暂无已提交日报'}
        />
      );
    }
    return departmentItems.map((report) => (
      <MobileReportCard
        key={report.id}
        report={report}
        subtitle={`${report.user.name} · ${formatLeadershipTier(report.user.leadershipTier ?? 'NONE')} · ${departmentNameMap.get(report.user.departmentId) ?? ''}`}
        onClick={() => navigate(ROUTES.mobile.leader.teamReportDetail(report.id))}
      />
    ));
  }, [departmentItems, departmentNameMap, loading, navigate, dateKey]);

  return (
    <div>
      <div className="mobile-filter-panel">
        <div className="mobile-filter-row">
          <span className="mobile-filter-label">日期</span>
          <DatePicker
            allowClear={false}
            value={reportDate}
            onChange={(value) => value && setReportDate(value)}
            inputReadOnly
            style={{ flex: 1 }}
          />
        </div>
        <button
          type="button"
          className="mobile-btn mobile-btn--primary mobile-btn--block"
          onClick={() => void loadReports()}
        >
          查询
        </button>
      </div>

      <section className="mobile-section">
        <div className="mobile-section__header">
          <span className="mobile-section__title">单位负责人日报</span>
          <span className="mobile-section__meta">{loading ? '…' : `${departmentItems.length} 人`}</span>
        </div>
        {reportList}
      </section>
    </div>
  );
}
